import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { isAxiosError } from 'axios'
import { axiosInstance } from '../axios-instance'
import {
  GuestDeleteConflict,
  guestContactShape,
  guestDeleteModeEnum,
  guestDocumentsShape,
  reservationGuestShape,
  surveyReviewShape,
} from './models'
import { z } from 'zod'

export const submitTRA = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: { reservationGuestId: z.string() },
    // The endpoint returns the tracked AsyncJob's id so the caller can show an
    // in-progress spinner immediately (see `useSubmitTraForGuest`).
    outputShape: { jobId: z.string() },
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post(
      `/stays/reservation-guests/${input.reservationGuestId}/submit-tra/`,
    )
    return utils.fromApi(res.data)
  },
})

// SIRE has no API — staff submit it manually on the Migración Colombia portal,
// which does not reliably return confirmation codes. These calls let staff
// explicitly confirm (or reverse) that submission without entering any codes.
export const submitSire = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: { reservationGuestId: z.string() },
    outputShape: { status: z.string() },
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post(
      `/stays/reservation-guests/${input.reservationGuestId}/submit-sire/`,
    )
    return utils.fromApi(res.data)
  },
})

export const unsubmitSire = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: { reservationGuestId: z.string() },
    outputShape: { status: z.string() },
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.post(
      `/stays/reservation-guests/${input.reservationGuestId}/unsubmit-sire/`,
    )
    return utils.fromApi(res.data)
  },
})

// Escalate a quick-added guest into the SIRE/TRA flow with a completed survey.
export const attachSurvey = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: { reservationGuestId: z.string(), instanceId: z.string() },
    outputShape: reservationGuestShape,
  },
  cb: async ({ client, input, utils }) => {
    // toApi validates the whole input shape, so convert all of it and send the body part.
    const { instance_id } = utils.toApi(input)
    const res = await client.post(
      `/stays/reservation-guests/${input.reservationGuestId}/attach-survey/`,
      { instance_id },
    )
    return utils.fromApi(res.data)
  },
})

// Edit the guest's contact through its reservation row; the server applies the
// row's edit phase (a `duplicate` edit returns a new reservation guest).
export const updateContact = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: { reservationGuestId: z.string(), contact: z.object(guestContactShape) },
    outputShape: reservationGuestShape,
  },
  cb: async ({ client, input, utils }) => {
    const { contact } = utils.toApi(input)
    const res = await client.patch(`/stays/reservation-guests/${input.reservationGuestId}/`, {
      contact,
    })
    return utils.fromApi(res.data)
  },
})

export const reservationGuestApi = createApi({
  client: axiosInstance,
  baseUri: '/stays/reservation-guests/',
  models: {
    entity: reservationGuestShape,
  },
})

// Staff read of a guest's survey without the guest's PIN (permission-checked and
// audited server-side). Never cache the result outside memory.
export const getSurveyReview = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: { reservationGuestId: z.string() },
    outputShape: surveyReviewShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.get(
      `/stays/reservation-guests/${input.reservationGuestId}/survey-review/`,
    )
    return utils.fromApi(res.data)
  },
})

// The object keys of a guest's passport photo page and entry stamp (permission-checked
// and audited server-side), to open through the presigned download.
export const getGuestDocuments = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: { reservationGuestId: z.string() },
    outputShape: guestDocumentsShape,
  },
  cb: async ({ client, input, utils }) => {
    const res = await client.get(`/stays/reservation-guests/${input.reservationGuestId}/documents/`)
    return utils.fromApi(res.data)
  },
})

// Delete one guest: `scrub` keeps the row as filing proof, `permanent` removes it.
// A filed or receipt-bearing guest needs `confirmed` for a permanent delete, else
// the server answers 409 with a `GuestDeleteConflict` body.
export const deleteGuest = createCustomServiceCall.standAlone({
  client: axiosInstance,
  models: {
    inputShape: {
      reservationGuestId: z.string(),
      mode: z.enum(guestDeleteModeEnum),
      confirmed: z.boolean().optional(),
    },
  },
  cb: async ({ client, input }) => {
    await client.delete(`/stays/reservation-guests/${input.reservationGuestId}/`, {
      params: { mode: input.mode, ...(input.confirmed ? { confirmed: 'true' } : {}) },
    })
  },
})

type RawFiling = { system: string; confirmation_code?: string | null; submitted_at?: string | null }

/** The `GuestDeleteConflict` carried by a 409 from `deleteGuest`, else null. */
export const guestDeleteConflictFromError = (error: unknown): GuestDeleteConflict | null => {
  if (!isAxiosError(error) || error.response?.status !== 409) return null
  const data = error.response.data ?? {}
  return {
    code: data.code === 'already_scrubbed' ? 'already_scrubbed' : 'guest_filed',
    hasReceipts: Boolean(data.has_receipts),
    filings: ((data.filings ?? []) as RawFiling[]).map((filing) => ({
      system: filing.system,
      confirmationCode: filing.confirmation_code ?? null,
      submittedAt: filing.submitted_at ?? null,
    })),
  }
}
